import React, { useState } from 'react'
import moment from 'moment'
import s from './Generator.module.scss'
import { openNotification } from '../notifications/notification'
import {
    generateDemoData,
    appendTodayDemoData,
    clearDemoData,
    getDemoSettings,
    PRICE_PRINT,
    PRICE_COPY,
    DEMO_DEVICE_COUNT,
    DAYS_PRESETS,
    DEFAULT_DAYS,
    GenerateResult,
    AppendResult,
} from '../../store/demo/demoGenerator'

const SUM_PRESETS = [20000, 35000, 50000, 75000, 120000]

const Generator: React.FC = () => {
    const initialSettings = getDemoSettings()
    const [value, setValue] = useState<string>(String(initialSettings.monthlyPerDevice || 50000))
    const [days, setDays] = useState<number>(initialSettings.days || DEFAULT_DAYS)
    const [result, setResult] = useState<GenerateResult | null>(null)
    const [appendResult, setAppendResult] = useState<AppendResult | null>(null)
    const [settings, setSettings] = useState(initialSettings)

    const parseTarget = () => {
        const n = Number(String(value).replace(/\s/g, '').replace(',', '.'))
        return Number.isFinite(n) ? Math.round(n) : NaN
    }

    const fmt = (n: number) => n.toLocaleString('ru-RU')

    const handleGenerate = () => {
        const target = parseTarget()
        if (!Number.isFinite(target) || target < 1000) {
            openNotification({ type: 'warning', message: 'Введите месячную норму от 1 000 ₽' })
            return
        }
        const res = generateDemoData(target, days)
        setResult(res)
        setAppendResult(null)
        setSettings(getDemoSettings())
        openNotification({ type: 'success', message: `История за ${days} дн. сгенерирована и сохранена` })
    }

    const handleToday = () => {
        const res = appendTodayDemoData()
        setAppendResult(res)
        setSettings(getDemoSettings())
        openNotification({ type: 'success', message: `Добавлено операций за сегодня: ${res.addedChecks}` })
    }

    const handleClear = () => {
        if (!window.confirm('Очистить все сгенерированные данные и сбросить к значениям по умолчанию?')) return
        const res = clearDemoData()
        setResult(null)
        setAppendResult(null)
        setValue(String(res.monthlyPerDevice))
        setDays(res.days)
        setSettings(getDemoSettings())
        openNotification({ type: 'success', message: 'Данные очищены и сброшены к дефолту' })
    }

    const dailyNorm = Math.round((parseTarget() || 0) / 30)

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <span className={s.accent}>Генератор</span> демо-данных
            </div>
            <div className={s.subtitle}>
                Сумма — месячная норма выручки на один аппарат; дневная плотность = норма / 30.
                Чеки правдоподобные по прайсу CTRL+C. Данные сохраняются — при навигации по демо
                цифры остаются теми же, меняются только по кнопкам ниже.
            </div>

            <div className={s.price}>
                <span className={s.chip}>
                    Печать: <b>{PRICE_PRINT} ₽/лист</b>
                </span>
                <span className={s.chip}>
                    Копир: <b>{PRICE_COPY} ₽/лист</b>
                </span>
                <span className={s.chip}>
                    Аппаратов в сети: <b>{DEMO_DEVICE_COUNT}</b>
                </span>
            </div>

            <div className={s.card}>
                {/* Месячная норма */}
                <div className={s.field}>
                    <label className={s.label}>Сумма операций за месяц на один аппарат (месячная норма)</label>
                    <div className={s.inputRow}>
                        <input
                            className={s.input}
                            type="number"
                            min={1000}
                            step={1000}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder="например, 50000"
                        />
                        <span className={s.suffix}>₽ / мес</span>
                    </div>
                    <div className={s.hint}>
                        Дневная плотность = норма / 30 ≈ {fmt(dailyNorm)} ₽/день на аппарат. Итог за
                        период — близко к норме×дней/30, с реалистичным разбросом ±~7%.
                    </div>
                </div>

                <div className={s.presets}>
                    {SUM_PRESETS.map((p) => (
                        <button key={p} className={s.preset} onClick={() => setValue(String(p))}>
                            {fmt(p)} ₽
                        </button>
                    ))}
                </div>

                <div className={s.divider} />

                {/* Период истории */}
                <div className={s.field}>
                    <label className={s.label}>Период — за сколько дней истории сгенерировать</label>
                    <div className={s.inputRow}>
                        <input
                            className={s.input}
                            type="number"
                            min={1}
                            max={365}
                            step={1}
                            value={days}
                            onChange={(e) => setDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
                            placeholder="например, 30"
                        />
                        <span className={s.suffix}>дней</span>
                    </div>
                    <div className={s.hint}>
                        «Сгенерировать» строит историю за выбранный период (заканчивая вчера).
                    </div>
                </div>

                <div className={s.daysPresets}>
                    {DAYS_PRESETS.map((d) => (
                        <button
                            key={d}
                            className={`${s.daysPreset} ${days === d ? s.active : ''}`}
                            onClick={() => setDays(d)}
                        >
                            {d} дн.
                        </button>
                    ))}
                </div>

                <div className={s.actions}>
                    <button className={`${s.btn} ${s.btnPrimary}`} onClick={handleGenerate}>
                        <span>Сгенерировать</span>
                    </button>
                    <button className={`${s.btn} ${s.btnAccent}`} onClick={handleToday}>
                        <span>Нагенерить за сегодня</span>
                    </button>
                    <button className={`${s.btn} ${s.btnGhost}`} onClick={handleClear}>
                        <span>Очистить данные</span>
                    </button>
                </div>
            </div>

            {result ? (
                <div className={s.result}>
                    <div className={s.resultHead}>Готово — история за {result.days} дн. сохранена</div>
                    <div className={s.grid}>
                        <div className={s.stat}>
                            <div className={`${s.statValue} ${s.lime}`}>{fmt(result.avgPerDevice)} ₽</div>
                            <div className={s.statLabel}>В среднем на аппарат за период</div>
                        </div>
                        <div className={s.stat}>
                            <div className={s.statValue}>{fmt(result.networkTotal)} ₽</div>
                            <div className={s.statLabel}>Итого по сети за период</div>
                        </div>
                        <div className={s.stat}>
                            <div className={s.statValue}>{fmt(result.totalChecks)}</div>
                            <div className={s.statLabel}>Чеков сгенерировано</div>
                        </div>
                        <div className={s.stat}>
                            <div className={s.statValue}>{fmt(result.dailyNorm)} ₽</div>
                            <div className={s.statLabel}>Дневная норма на аппарат</div>
                        </div>
                    </div>
                    <div className={s.note}>
                        Период: {moment(result.periodBeg).format('DD.MM.YYYY')} —{' '}
                        {moment(result.periodEnd).format('DD.MM.YYYY')}. Откройте операции любого
                        аппарата на экране «Устройства» — суммы уже обновлены.
                    </div>
                </div>
            ) : null}

            {appendResult ? (
                <div className={s.result}>
                    <div className={s.resultHead}>Добавлено за сегодня ({appendResult.date})</div>
                    <div className={s.grid}>
                        <div className={s.stat}>
                            <div className={`${s.statValue} ${s.lime}`}>+{fmt(appendResult.addedChecks)}</div>
                            <div className={s.statLabel}>Чеков добавлено (по всей сети)</div>
                        </div>
                        <div className={s.stat}>
                            <div className={s.statValue}>+{fmt(appendResult.addedSum)} ₽</div>
                            <div className={s.statLabel}>Сумма за сегодня по сети</div>
                        </div>
                        <div className={s.stat}>
                            <div className={s.statValue}>{fmt(appendResult.dailyNorm)} ₽</div>
                            <div className={s.statLabel}>Дневная норма на аппарат</div>
                        </div>
                        <div className={s.stat}>
                            <div className={s.statValue}>{appendResult.deviceCount}</div>
                            <div className={s.statLabel}>Аппаратов обновлено</div>
                        </div>
                    </div>
                    <div className={s.note}>
                        Операции за сегодня дописаны к истории (время — в пределах сегодняшнего дня
                        до текущего момента). История не стёрта.
                    </div>
                </div>
            ) : null}

            <div className={s.meta}>
                Текущие данные: {fmt(settings.monthlyPerDevice)} ₽/мес на аппарат · период{' '}
                {settings.days} дн. ({moment(settings.periodBeg).format('DD.MM.YYYY')} —{' '}
                {moment(settings.periodEnd).format('DD.MM.YYYY')})
                {settings.generatedAt
                    ? ` · сгенерировано ${moment(settings.generatedAt).format('DD.MM.YYYY HH:mm')}`
                    : ''}
                {settings.lastAppendAt
                    ? ` · «за сегодня» ${moment(settings.lastAppendAt).format('DD.MM.YYYY HH:mm')}`
                    : ''}
            </div>
        </div>
    )
}

export default Generator
